import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import ShareableListView from '~/client/components/shareable-list/ShareableListView'
import { EditShareableListDialog, ShareableListDialogMode } from '~/client/components/shareable-list/EditShareableListDialog'
import { useShareableList } from '~/client/shareable-list/use-shareable-list.hook'
import { seo } from '~/client/common/seo'
import AppHeader from '~/client/components/AppHeader'
import PublicAppHeader from '~/client/components/PublicAppHeader'
import { useAdminUserDetail, useAuthStore, useEffectiveUserDetail } from '~/client/common/stores/auth'
import { supabaseClient } from '~/server/common/supabase/client'
import { fetchShareableListPageData } from '~/server/shareable-list/shareable-list.server'

/**
 * Derive a stable `isLoggedIn` value.
 *
 * Priority:
 * 1. If the auth store is already initialized (e.g. user navigated here from
 *    an authenticated route), use its state immediately — no extra getSession()
 *    call needed.
 * 2. Otherwise, do a one-time getSession() check and kick off initializeAuth()
 *    in the background.
 *
 * Returns `null` while still resolving, `true`/`false` once known.
 */
function useAuthDetection(): boolean | null {
  const storeInitialized = useAuthStore((s) => s.initialized)
  const storeUser = useAuthStore((s) => s.user)

  // If the store is already initialized we can answer synchronously.
  const fromStore: boolean | null = storeInitialized ? !!storeUser : null

  const [checked, setChecked] = React.useState<boolean | null>(fromStore)

  // Keep in sync when the store initializes later (e.g. after the effect below)
  React.useEffect(() => {
    if (storeInitialized) {
      setChecked(!!storeUser)
    }
  }, [storeInitialized, storeUser])

  // One-time session check — only when the store hasn't been initialized yet
  React.useEffect(() => {
    if (storeInitialized) return // already known
    let cancelled = false

    supabaseClient.auth.getSession().then(({ data: sessionData }) => {
      if (cancelled) return
      const hasSession = !!sessionData.session
      setChecked(hasSession)
      if (hasSession) {
        useAuthStore.getState().initializeAuth().catch(console.error)
      }
    })

    return () => { cancelled = true }
  }, [storeInitialized])

  return checked
}

type CollectionSearch = {
  space?: string
  tab?: 'search' | 'collection'
  googlePlaceId?: string
  venueName?: string
}

const rb2bKey = import.meta.env.VITE_RB2B_KEY

const rb2bScriptContent = rb2bKey
  ? `!function(key){if(window.reb2b)return;window.reb2b={loaded:true};var s=document.createElement("script");s.async=true;s.src="https://ddwl4m2hdecbv.cloudfront.net/b/"+key+"/"+key+".js.gz";document.getElementsByTagName("script")[0].parentNode.insertBefore(s,document.getElementsByTagName("script")[0]);}("${rb2bKey}");`
  : null

export const Route = createFileRoute('/collection/$slug')({
  ssr: true,
  validateSearch: (search: Record<string, unknown>): CollectionSearch => ({
    space: typeof search.space === 'string' && search.space ? search.space : undefined,
    tab: search.tab === 'search' || search.tab === 'collection' ? search.tab : undefined,
    googlePlaceId: typeof search.googlePlaceId === 'string' && search.googlePlaceId ? search.googlePlaceId : undefined,
    venueName: typeof search.venueName === 'string' && search.venueName ? search.venueName : undefined,
  }),
  loader: ({ params }) => fetchShareableListPageData({ data: { slug: params.slug } }),
  head: ({ loaderData }) => {
    const meta = loaderData?.meta
    const baseHead = {
      ...(rb2bScriptContent
        ? { scripts: [{ children: rb2bScriptContent }] }
        : {}),
    }
    if (!meta) {
      return {
        ...baseHead,
        meta: seo({
          title: 'Revel Street - Curated Spaces',
          description: 'A curated collection of event spaces',
        }),
      }
    }
    const city = meta.location?.split(',')[0]?.trim() || 'your city'
    const title = `${meta.name} - Revel Street`
    const description = meta.criteria || `A curated collection of venues in ${city}`
    return { ...baseHead, meta: seo({ title, description }) }
  },
  component: ShareableListPage,
})

function ShareableListPage() {
  const { slug } = Route.useParams()
  const loaderData = Route.useLoaderData()
  const meta = loaderData?.meta ?? null
  const initialData = loaderData?.data ?? null
  const navigate = useNavigate()
  const isLoggedIn = useAuthDetection()
  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [isCloneOpen, setIsCloneOpen] = React.useState(false)
  const { data, isLoading, error } = useShareableList(slug, isLoggedIn, initialData)
  // isOwner reflects "does the user being viewed own this list" — so an admin
  // impersonating the list's real owner sees the edit affordance (and can act on
  // it via impersonation-aware writes).
  const effectiveUser = useEffectiveUserDetail()
  const isOwner = isLoggedIn === true && !!data && data.update_author_id === effectiveUser?.id
  const adminUser = useAdminUserDetail()
  const canClone = isLoggedIn === true && !!adminUser?.email?.toLowerCase().endsWith('@revelstreet.co')

  const renderContent = () => {
    if (isLoading) {
      return <ShareableListView isLoading />
    }

    if (error || !data) {
      return (
        <div className="flex items-center justify-center h-full bg-[#fefbf6]">
          <div className="text-center">
            <h1 className="font-['Inter:Bold',_sans-serif] font-bold text-[24px] text-[#18003d]">
              List not found
            </h1>
            <p className="font-['Inter:Regular',_sans-serif] text-[#95919a] text-[16px] mt-2">
              This shareable list does not exist or has been removed.
            </p>
          </div>
        </div>
      )
    }

    return (
      <ShareableListView
        data={data}
        isOwner={isOwner}
        onEdit={() => setIsEditOpen(true)}
        onClone={canClone ? () => setIsCloneOpen(true) : undefined}
        plannerName={meta?.authorName ?? null}
        plannerTitle={meta?.authorTitle ?? null}
      />
    )
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {isLoggedIn === true ? <AppHeader /> : <PublicAppHeader />}
      <div className="w-full h-full flex-1 min-h-0">
        {renderContent()}
      </div>
      {data && isOwner && (
        <EditShareableListDialog
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          listId={data.id}
          currentName={data.name}
          currentDescription={data.criteria}
          onSaved={(newSlug) => {
            navigate({ to: '/collection/$slug', params: { slug: newSlug } })
          }}
          onDeleted={() => {
            navigate({ to: '/' })
          }}
        />
      )}
      {data && canClone && (
        <EditShareableListDialog
          open={isCloneOpen}
          onOpenChange={setIsCloneOpen}
          mode={ShareableListDialogMode.CLONE}
          listId={data.id}
          currentName={data.name}
          currentDescription={data.criteria}
          onSaved={(newSlug) => {
            navigate({ to: '/collection/$slug', params: { slug: newSlug } })
          }}
        />
      )}
    </div>
  )
}
